import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { toast, Toaster } from "sonner";
import { Heart, ShoppingCart, Trash2, Share2 } from "lucide-react";

export default function WishlistPage() {
    const { user } = useAuth();
    const { addToCart } = useCart();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [details, setDetails] = useState({});  // map id -> full product data
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) { setLoading(false); return; }
        Promise.all([
            api.get("/wishlist").then(({ data }) => data),
            api.get("/templates").then(({ data }) => data).catch(() => []),
            api.get("/tshirts").then(({ data }) => data).catch(() => []),
        ]).then(([wishlist, templates, tshirts]) => {
            const map = {};
            templates.forEach(t => { map[`template-${t.id}`] = { ...t, _type: "template" }; });
            tshirts.forEach(t => { map[`tshirt-${t.id}`] = { ...t, _type: "tshirt" }; });
            setDetails(map);
            setItems(wishlist);
        }).finally(() => setLoading(false));
    }, [user]);

    const remove = async (item) => {
        try {
            await api.delete(`/wishlist/${item.item_type}/${item.item_id}`);
            setItems(prev => prev.filter(x => x.id !== item.id));
            toast.success("Verwijderd uit favorieten");
        } catch {
            toast.error("Kon niet verwijderen");
        }
    };

    const buyTemplate = async (tpl) => {
        const size = tpl.available_sizes?.[0];
        if (!size) return;
        try {
            await addToCart({ template_id: tpl.id, size }, "template");
            toast.success(`${tpl.name} (${size}) toegevoegd aan mandje`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Toevoegen mislukt");
        }
    };

    const shareViaWhatsApp = () => {
        if (items.length === 0) {
            toast.error("Voeg eerst iets toe aan je lijstje");
            return;
        }
        const lines = items.slice(0, 12).map((w, i) => {
            const d = details[`${w.item_type}-${w.item_id}`];
            const name = d?.name || "(item)";
            const price = d?.price ? ` — € ${Number(d.price).toFixed(2)}` : "";
            return `${i + 1}. ${name}${price}`;
        }).join("\n");
        const userName = user?.name ? user.name.split(" ")[0] : "Iemand";
        const url = window.location.origin + "/stickers";
        const text = `Hallo! 🎁\n\n${userName} heeft een cadeau-lijstje op VdS Gift Shop:\n\n${lines}\n\n👉 Bekijk alles op ${url}\n\n(Verstuurd via mijn wishlist op VdS Gift Shop)`;
        const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(waUrl, "_blank", "noopener,noreferrer");
        toast.success("WhatsApp geopend met je lijstje");
    };

    if (!user) {
        return (
            <div className="min-h-[60vh] grid place-items-center p-10 text-center">
                <div>
                    <p className="label-tiny">Favorieten</p>
                    <h1 className="font-display text-4xl mt-2">Log in om je favorieten te zien.</h1>
                    <Link to="/login" className="btn-primary mt-6 inline-block">Inloggen</Link>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="wishlist-page" className="bg-zinc-50 min-h-screen">
            <Toaster />
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                <p className="label-tiny flex items-center gap-1"><Heart size={12} /> Favorieten</p>
                <div className="mt-2 flex items-end justify-between flex-wrap gap-3">
                    <h1 className="font-display text-5xl">Mijn lijstje <span className="text-zinc-400 text-3xl">({items.length})</span></h1>
                    {items.length > 0 && (
                        <button onClick={shareViaWhatsApp} data-testid="wishlist-share-whatsapp"
                            className="btn-rose inline-flex items-center gap-2">
                            <Share2 size={14} /> Deel via WhatsApp
                        </button>
                    )}
                </div>

                {loading ? (
                    <p className="text-zinc-500 mt-10">Laden…</p>
                ) : items.length === 0 ? (
                    <div className="mt-10 border border-dashed border-zinc-300 bg-white p-12 text-center">
                        <Heart size={32} className="mx-auto text-zinc-300" />
                        <p className="text-zinc-500 mt-3">Nog geen favorieten.</p>
                        <p className="text-zinc-500 text-sm mt-1">Klik op het hartje bij een design om hem hier te bewaren.</p>
                        <Link to="/designs" className="btn-primary mt-6 inline-block">Bekijk vaste designs</Link>
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-zinc-200">
                        {items.map(w => {
                            const d = details[`${w.item_type}-${w.item_id}`];
                            if (!d) return (
                                <div key={w.id} className="border-r border-b border-zinc-200 p-6 bg-white text-sm text-zinc-400 italic">
                                    Item is niet meer beschikbaar
                                    <button onClick={() => remove(w)} className="block text-xs mt-2 text-rose-600">Verwijder</button>
                                </div>
                            );
                            return (
                                <div key={w.id} data-testid={`wishlist-item-${w.id}`} className="border-r border-b border-zinc-200 bg-white p-6">
                                    <div className="aspect-square bg-zinc-50 border border-zinc-200 overflow-hidden">
                                        <img src={d.preview_image_url || d.image_url} alt={d.name} className="w-full h-full object-contain" />
                                    </div>
                                    <div className="mt-4 flex items-baseline justify-between">
                                        <p className="font-display text-lg">{d.name}</p>
                                        <p className="font-mono-dm text-sm">€ {(d.price || 0).toFixed(2)}</p>
                                    </div>
                                    <p className="label-tiny mt-1">{w.item_type === "template" ? "Vast design" : "T-shirt"}</p>
                                    <div className="mt-4 flex gap-2">
                                        {w.item_type === "template" ? (
                                            <button onClick={() => buyTemplate(d)} data-testid={`wishlist-buy-${w.id}`}
                                                className="btn-primary text-xs flex items-center gap-1.5 flex-1 justify-center">
                                                <ShoppingCart size={13} /> Bestel
                                            </button>
                                        ) : (
                                            <button onClick={() => navigate("/designer")} data-testid={`wishlist-design-${w.id}`}
                                                className="btn-primary text-xs flex-1">Ontwerp</button>
                                        )}
                                        <button onClick={() => remove(w)} data-testid={`wishlist-remove-${w.id}`}
                                            className="btn-ghost text-xs text-rose-600 border-rose-200 flex items-center gap-1">
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
